import {
  Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, makeStyles, Step, StepLabel, Stepper, Typography,
} from '@material-ui/core';
import { ChangeEvent, useEffect, useState } from 'react';

import SquareStepIcon from '../../../shared/components/Steppers/SquareStepIcon.component';
import DeliveryPointSelectionCard from '../Forms/DeliveryPointSelectionCard.component';

import { Organization } from '../../../organizations/types/organization.type';
import { useCreateOrganizationInvoice } from '../../hooks/create-organization-invoice.hook';
import { DeliveryPointsScope, DeliveryPointsScopes } from '../../types/delivery-points-scope.type';
import {
  getDefaultInvoiceGenerationSettings, hasInvalidInterval, InvoiceGenerationSettingsFields,
} from '../Forms/InvoiceGenerationSettingsFields.component';

type Props = {
  open: boolean;
  organization: Organization;
  onClose: () => void;
  onCreated: () => void;
};

const STEPS = ['Paramètres', 'Points de livraison'];

const useStyles = makeStyles((theme) => ({
  stepper: {
    padding: '0 0 1rem 0',
  },
  deliveryPointsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  selectionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    paddingBottom: '0.25rem',
  },
  selectionCount: {
    fontSize: 13,
    opacity: 0.7,
  },
  emptyMessage: {
    opacity: 0.7,
  },
  submitButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

function getAllDeliveryPointIds(organization: Organization): string[] {
  return organization.deliveryPoints.map((deliveryPoint) => deliveryPoint.id);
}

export function CreateOrganizationInvoiceModal({ open, organization, onClose, onCreated }: Props) {
  const classes = useStyles();
  const { deliveryPoints } = organization;

  const [activeStep, setActiveStep] = useState(0);
  const [settings, setSettings] = useState(getDefaultInvoiceGenerationSettings());
  const [scope, setScope] = useState<DeliveryPointsScope>(DeliveryPointsScopes.ALL);
  const [selectedIds, setSelectedIds] = useState<string[]>(getAllDeliveryPointIds(organization));

  const { createInvoice, isLoading } = useCreateOrganizationInvoice();

  // Dépendance volontairement limitée à [open] : la closure lit l'`organization` courante
  // à chaque (ré)ouverture, un ajout de `organization` aux dépendances n'est pas nécessaire.
  useEffect(() => {
    setActiveStep(0);
    setSettings(getDefaultInvoiceGenerationSettings());
    setScope(DeliveryPointsScopes.ALL);
    setSelectedIds(getAllDeliveryPointIds(organization));
  }, [open]);

  const handleToggleAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setScope(DeliveryPointsScopes.ALL);
      setSelectedIds(getAllDeliveryPointIds(organization));
    } else {
      setScope(DeliveryPointsScopes.SELECTION);
      setSelectedIds([]);
    }
  };

  const handleTogglePoint = (deliveryPointId: string, checked: boolean) => {
    if (scope === DeliveryPointsScopes.ALL) {
      // En portée ALL toutes les cases sont rendues cochées : un clic ne peut donc signifier
      // qu'un décochage, `checked` n'a pas besoin d'être lu ici.
      setScope(DeliveryPointsScopes.SELECTION);
      setSelectedIds(getAllDeliveryPointIds(organization).filter((id) => id !== deliveryPointId));
      return;
    }
    setSelectedIds((previous) => (
      checked
        ? [...previous, deliveryPointId]
        : previous.filter((id) => id !== deliveryPointId)
    ));
  };

  const handleSubmit = async () => {
    const created = await createInvoice({
      organizationId: organization.id,
      ...settings,
      deliveryPointsScope: scope,
      deliveryPointIds: scope === DeliveryPointsScopes.ALL ? [] : selectedIds,
    });
    if (created) {
      onCreated();
      onClose();
    }
  };

  const isSettingsStepInvalid = hasInvalidInterval(settings) || !settings.from || !settings.to;
  const isAllChecked = scope === DeliveryPointsScopes.ALL;
  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nouvelle facture</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={SquareStepIcon}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <InvoiceGenerationSettingsFields value={settings} onChange={setSettings} />
        )}

        {activeStep === 1 && (
          deliveryPoints.length === 0 ? (
            <Typography className={classes.emptyMessage}>
              Cette organisation n&apos;a aucun point de livraison.
            </Typography>
          ) : (
            <div className={classes.deliveryPointsList}>
              <div className={classes.selectionBar}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={isAllChecked}
                      // Trois états : une sélection partielle se lit d'un coup d'œil.
                      indeterminate={!isAllChecked && selectedIds.length > 0}
                      onChange={handleToggleAll}
                      color="default"
                    />
                  )}
                  label="Tous les points de livraison"
                />
                <Typography className={classes.selectionCount}>
                  {`${isAllChecked ? deliveryPoints.length : selectedIds.length} / ${deliveryPoints.length} sélectionnés`}
                </Typography>
              </div>
              {deliveryPoints.map((deliveryPoint) => (
                <DeliveryPointSelectionCard
                  key={deliveryPoint.id}
                  deliveryPoint={deliveryPoint}
                  isSelected={isAllChecked || selectedIds.includes(deliveryPoint.id)}
                  onToggle={(isSelected) => handleTogglePoint(deliveryPoint.id, isSelected)}
                />
              ))}
            </div>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Annuler</Button>
        {activeStep > 0 && (
          <Button onClick={() => setActiveStep((previous) => previous - 1)} disabled={isLoading}>Précédent</Button>
        )}
        {!isLastStep && (
          <Button
            className={classes.submitButton}
            onClick={() => setActiveStep((previous) => previous + 1)}
            variant="contained"
            disabled={isSettingsStepInvalid}
          >
            Suivant
          </Button>
        )}
        {isLastStep && (
          <Button
            className={classes.submitButton}
            onClick={handleSubmit}
            variant="contained"
            disabled={isLoading || selectedIds.length === 0}
          >
            {isLoading ? 'Génération…' : 'Générer'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
